// Página pública do link do parceiro (#6511): #/parceiro/<código>.
//
// O cliente deixa nome, WhatsApp, cidade e conta de luz, sem login. A página grava na
// pre_venda (a mesma porta do simulador do site) com o código em `indicado_por`; o
// gatilho do banco cria a negociação no nome do parceiro, fora do rodízio, e o parceiro
// recebe o aviso no sino. Código inválido ou de parceiro inativo vira lead comum do
// site — por isso o formulário aparece mesmo quando o nome do parceiro não vem.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { TIPOS_CONTA, faltaNoLead, linhaPreVenda, normalizarCodigo, type DadosDoLead } from '../../src/lib/linkParceiro';

const VAZIO: DadosDoLead = { nome: '', whatsapp: '', cidade: '', tipo: '', valorConta: '' };

/** "MARIA" ou "maria" → "Maria". */
const capitalizar = (nome: string) => nome.charAt(0).toUpperCase() + nome.slice(1).toLowerCase();

const campo = 'block w-full rounded-xl border border-gray-300 bg-white px-3 py-3 text-base text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';

const LinkParceiroView: React.FC = () => {
  const { codigo: codigoDaUrl } = useParams<{ codigo: string }>();
  const codigo = normalizarCodigo(codigoDaUrl);
  const [parceiro, setParceiro] = useState<string | null>(null);
  const [dados, setDados] = useState<DadosDoLead>(VAZIO);
  const [armadilha, setArmadilha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  // Primeiro nome do parceiro, para o cliente saber quem vai chamar. Sem ele, a página
  // funciona igual (o lead segue o caminho do site).
  useEffect(() => {
    if (!codigo) return;
    let vivo = true;
    supabase.rpc('fn_parceiro_do_link', { p_codigo: codigo }).then(({ data }) => {
      const nome = Array.isArray(data) ? data[0]?.primeiro_nome : null;
      if (vivo && nome) setParceiro(capitalizar(String(nome)));
    });
    return () => { vivo = false; };
  }, [codigo]);

  const mudar = (k: keyof DadosDoLead) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setDados(d => ({ ...d, [k]: e.target.value }));
    setErro(null);
  };

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (enviando) return;
    const falta = faltaNoLead(dados);
    if (falta) { setErro(falta); return; }
    // Campo escondido que só robô preenche: finge que deu certo e não grava nada.
    if (armadilha) { setEnviado(true); return; }
    setEnviando(true);
    setErro(null);
    const { error } = await supabase.from('pre_venda').insert(linhaPreVenda(dados, codigo));
    setEnviando(false);
    if (error) {
      console.error('Link do parceiro: falha ao gravar o lead', error);
      setErro('Não conseguimos enviar agora. Confira sua internet e tente de novo.');
      return;
    }
    setEnviado(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-md mx-auto px-4 h-16 flex items-center">
          <img src="/LOGO-01.png" alt="Vigor Energy" className="h-10 sm:h-12 w-auto object-contain" />
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-5">
        {enviado ? (
          <section role="status" className="rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 size={28} className="text-green-600" />
            </div>
            <h1 className="mt-4 text-xl font-bold text-gray-900">Recebemos seus dados!</h1>
            <p className="mt-2 text-sm text-gray-600">
              {parceiro ? `${parceiro}, da Vigor Energy, vai` : 'Nossa equipe vai'} chamar você no WhatsApp em breve para mostrar quanto você pode economizar.
            </p>
          </section>
        ) : (
          <>
            <section className="space-y-2">
              {parceiro && (
                <p className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                  Indicação de {parceiro}
                </p>
              )}
              <h1 className="text-2xl font-bold text-gray-900">Pague menos na conta de luz</h1>
              <p className="text-sm text-gray-600">
                Com a energia solar por assinatura da Vigor Energy você não faz obra e não instala nada no seu imóvel.
                Deixe seus dados que {parceiro ? `${parceiro} chama` : 'a gente chama'} você no WhatsApp.
              </p>
            </section>

            <form onSubmit={enviar} noValidate className="rounded-2xl border border-gray-100 border-t-4 border-t-primary bg-white p-5 shadow-sm space-y-4">
              <div>
                <label htmlFor="lead-nome" className="block text-sm font-medium text-gray-700 mb-1.5">Nome completo</label>
                <input id="lead-nome" type="text" autoComplete="name" value={dados.nome} onChange={mudar('nome')} className={campo} placeholder="Seu nome e sobrenome" />
              </div>
              <div>
                <label htmlFor="lead-whatsapp" className="block text-sm font-medium text-gray-700 mb-1.5">WhatsApp</label>
                <input id="lead-whatsapp" type="tel" inputMode="tel" autoComplete="tel" value={dados.whatsapp} onChange={mudar('whatsapp')} className={campo} placeholder="(62) 99999-9999" />
              </div>
              <div>
                <label htmlFor="lead-cidade" className="block text-sm font-medium text-gray-700 mb-1.5">Cidade</label>
                <input id="lead-cidade" type="text" autoComplete="address-level2" value={dados.cidade} onChange={mudar('cidade')} className={campo} placeholder="Sua cidade" />
              </div>
              <fieldset>
                <legend className="block text-sm font-medium text-gray-700 mb-1.5">A conta de luz é de</legend>
                <div className="grid grid-cols-3 gap-2">
                  {TIPOS_CONTA.map(t => (
                    <button
                      key={t}
                      type="button"
                      aria-pressed={dados.tipo === t}
                      onClick={() => { setDados(d => ({ ...d, tipo: t })); setErro(null); }}
                      className={`min-h-[44px] rounded-xl border px-2 text-sm font-medium transition-colors ${dados.tipo === t
                        ? 'border-primary bg-primary text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-primary'}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </fieldset>
              <div>
                <label htmlFor="lead-valor" className="block text-sm font-medium text-gray-700 mb-1.5">Valor médio da conta de luz</label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">R$</span>
                  <input id="lead-valor" type="text" inputMode="decimal" value={dados.valorConta} onChange={mudar('valorConta')} className={`${campo} pl-10`} placeholder="450,00" />
                </div>
              </div>

              {/* Armadilha para robô: escondida de quem enxerga e de leitor de tela. */}
              <div className="hidden" aria-hidden="true">
                <label htmlFor="lead-site">Site</label>
                <input id="lead-site" type="text" tabIndex={-1} autoComplete="off" value={armadilha} onChange={e => setArmadilha(e.target.value)} />
              </div>

              {erro && (
                <p role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{erro}</span>
                </p>
              )}

              <button
                type="submit"
                disabled={enviando}
                className="flex w-full min-h-[48px] items-center justify-center gap-2 rounded-xl bg-primary px-4 text-base font-bold text-white shadow-lg shadow-primary/25 transition-colors hover:bg-primary/90 disabled:opacity-70"
              >
                {enviando ? <><Loader2 size={18} className="animate-spin" /> Enviando...</> : <>Quero economizar <ArrowRight size={18} /></>}
              </button>
              <p className="text-xs text-gray-500">
                Ao enviar, você autoriza a Vigor Energy{parceiro ? ` e ${parceiro}` : ''} a falar com você pelo WhatsApp sobre a sua conta de luz.
              </p>
            </form>
          </>
        )}
      </main>
    </div>
  );
};

export default LinkParceiroView;
